import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Reverse-sync: when the visitor browses the gallery, keep the colour
    // dropdown and swatches matched to the image on screen.
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleGallerySelect, { signal });

    // Forward-sync: the swatches are a product-card component whose clicks don't
    // reach this product page's main picker. Bridge swatch clicks to the picker.
    target?.addEventListener('change', this.#handleSwatchChange, { signal });
  }

  #controller = new AbortController();

  // Media id of the slide the visitor last navigated to. Used to recognise the
  // variant:update we trigger ourselves, so the gallery isn't rebuilt under them.
  #navMediaId = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // The visitor navigated the gallery, which selected the matching colour, so
    // the image on screen already matches the new variant — don't rebuild. A
    // rebuild re-sorts the media and resets the carousel to the first slide,
    // which would break browsing through colours.
    const selectedMediaId = event.detail?.resource?.featured_media?.id;
    if (
      selectedMediaId != null &&
      this.#navMediaId != null &&
      String(selectedMediaId) === String(this.#navMediaId)
    ) {
      return;
    }

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Genuine variant change from elsewhere (a swatch/dropdown click): the
    // gallery is about to be rebuilt, so forget the last navigated slide.
    this.#navMediaId = null;
    this.replaceWith(newMediaGallery);
  };

  /**
   * Selects colour `value` on the main variant picker (dropdown or buttons),
   * which updates the dropdown display, price, URL and add-to-cart variant via
   * the theme's own variant logic. Returns true if a change was dispatched.
   *
   * @param {string} value
   * @returns {boolean}
   */
  #drivePicker(value) {
    const scope = this.closest('.shopify-section, dialog') ?? document;
    const picker = scope.querySelector('variant-picker');
    if (!picker) return false;

    const select = picker.querySelector('select');
    if (select) {
      if (select.value === value) return false;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    const radio = [...picker.querySelectorAll('input[type="radio"]')].find((r) => r.value === value);
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    return false;
  }

  /**
   * Reverse-sync: when the visitor browses the gallery, select the colour whose
   * image is shown. Each swatch radio carries its variant's featured media id,
   * so we map the on-screen image to its colour, reflect it on the swatch strip,
   * and drive the main picker. The shown media id is recorded so
   * #handleVariantUpdate doesn't rebuild the gallery the visitor is looking at.
   *
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleGallerySelect = (event) => {
    // Ignore the programmatic select() that fires when the gallery is rebuilt;
    // only react to user navigation (arrows, dots, thumbnails, swipe).
    if (!event.detail?.userInitiated) return;

    const mediaId = event.detail.slide
      ?.querySelector('[data-media-id]')
      ?.getAttribute('data-media-id');
    if (!mediaId) return;

    const scope = this.closest('.shopify-section, dialog') ?? document;
    const swatch = scope.querySelector(
      `swatches-variant-picker-component input[type="radio"][data-option-media-id="${mediaId}"]`
    );
    if (!(swatch instanceof HTMLInputElement) || swatch.checked) return;

    // The gallery already shows this colour's image; record it so the picker
    // change below doesn't trigger a (jarring) gallery rebuild.
    this.#navMediaId = mediaId;
    swatch.checked = true; // reflect on the swatch strip
    this.#drivePicker(swatch.value);
  };

  /**
   * Forward-sync: the swatch strip is a product-card component, so a swatch
   * click never reaches this page's main picker on its own. Bridge the click to
   * the picker, which updates the dropdown, price, cart variant and — by
   * rebuilding — the large image.
   *
   * @param {Event} event
   */
  #handleSwatchChange = (event) => {
    const input = event.target;
    if (
      !(input instanceof HTMLInputElement) ||
      input.type !== 'radio' ||
      !input.closest('swatches-variant-picker-component')
    ) {
      return;
    }
    // Not gallery-driven: leave #navMediaId unset so the gallery rebuilds to the
    // chosen colour's image.
    this.#drivePicker(input.value);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

